/**
 * RAG Chat Context
 *
 * Provides AI chat with RAG (Retrieval-Augmented Generation) for list views.
 * Searches across all articles in the current scope (all/inbox/specific list).
 *
 * Unlike AILearnContext (article-scoped), this context works at the list level
 * and uses vector similarity search to find relevant content across articles.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useObserve, useSelector } from '@legendapp/state/react';
import { apiConfig } from '../lib/apiConfig';
import { settings$, updateAIModel, updateAILanguage, updateFollowUILanguage, updateChatThinking, updateWebSearch } from '../store/settings$';
import {
  ragConversations$,
  getRAGConversation,
  addRAGMessage,
  clearRAGConversation,
  deleteRAGMessage,
  setRAGPreferredModel,
  setRAGPreferredLanguage,
} from '../store/ragConversations$';
import { auth$ } from '../store/auth$';
import { useCreditBalance } from '../hooks/useCreditBalance';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { openUsageModal } from '../store/ui$';
import { useStreamingChat, STREAMING_STATES } from '../hooks/useStreamingChat';

// Reuse models from AILearnContext, languages from centralized config
import { AI_MODELS } from './AILearnContext';
import { RESPONSE_LANGUAGES, UI_TO_AI_LANG_MAP } from '../constants/languages';
import { useTranslation } from 'react-i18next';

const RAGChatContext = createContext(null);

// Default AI model for RAG conversations
const DEFAULT_AI_MODEL = 'gemini-3-flash-preview';

// Default search sources (all enabled)
const DEFAULT_SEARCH_SOURCES = { articles: true, highlights: true, comments: true };

// Get stored search sources preference (global, not per-scope)
const getStoredSearchSources = () => {
  try {
    const stored = localStorage.getItem('reedle-rag-search-sources');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate structure
      if (typeof parsed.articles === 'boolean' &&
          typeof parsed.highlights === 'boolean' &&
          typeof parsed.comments === 'boolean') {
        return parsed;
      }
    }
  } catch {
    // Ignore parsing errors
  }
  return DEFAULT_SEARCH_SOURCES;
};

// Embedding models for A/B testing
const EMBEDDING_MODELS = [
  { id: 'qwen', name: 'Qwen3-8B', provider: 'deepinfra' },
  { id: 'openai', name: 'OpenAI Small', provider: 'openai' },
  { id: 'jina', name: 'Jina v3', provider: 'jina' },
];

// Default embedding model
const DEFAULT_EMBEDDING_MODEL = 'qwen';

// Get stored embedding model preference
const getStoredEmbeddingModel = () => {
  try {
    const stored = localStorage.getItem('reedle-rag-embedding-model');
    if (stored && ['qwen', 'openai', 'jina'].includes(stored)) {
      return stored;
    }
  } catch {
    // Ignore storage errors
  }
  return DEFAULT_EMBEDDING_MODEL;
};

// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const useRAGChat = () => {
  const context = useContext(RAGChatContext);
  return context;
};

/**
 * RAGChatProvider
 *
 * @param {string} scopeType - 'all' | 'inbox' | 'list' | 'deck'
 * @param {string|null} scopeId - List/Deck ID for 'list'/'deck' scope, null for 'all'/'inbox'
 * @param {string} userId - Current user ID
 * @param {string} [deckName] - Display name for deck scope
 */
export const RAGChatProvider = ({ children, scopeType, scopeId, userId, deckName }) => {
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(() => settings$.aiModel.get() || DEFAULT_AI_MODEL);
  const responseLanguage = useSelector(() => settings$.aiLanguage.get());
  const followUILanguage = useSelector(() => settings$.followUILanguage.get());
  const chatThinkingEnabled = useSelector(() => settings$.chatThinkingEnabled.get());
  const webSearchEnabled = useSelector(() => settings$.webSearchEnabled.get());
  const [quoteSources, setQuoteSources] = useState({});

  // RAG-specific state
  const [searchingArticles, setSearchingArticles] = useState(false);
  const [lastRetrievedSources, setLastRetrievedSources] = useState([]);

  // Search sources filter (global preference)
  const [searchSources, setSearchSources] = useState(getStoredSearchSources);

  // Embedding model for A/B testing (global preference)
  const [embeddingModel, setEmbeddingModel] = useState(getStoredEmbeddingModel);

  // Online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Streaming
  const {
    streamingState,
    isStreamingActive,
    forceCompleteStreaming,
    handleAnimationComplete,
    stopStreaming,
    streamBufferRef,
    visibleLengthRef,
    captureStreamingMode,
    prepareFetch,
    startStreaming,
    processStreamingResponse,
    cleanupStreaming,
    streamingMessageIndex,
    streamingEnabled,
    setStreamingEnabled,
  } = useStreamingChat();

  // Flag to defer assistant message persistence until FULLY_COMPLETE
  // (mirrors AILearnContext pattern — avoids useObserve race during streaming)
  const pendingStreamContentRef = useRef(false);
  // Sources from the done event, stored for the deferred persistence useEffect
  const pendingStreamSourcesRef = useRef([]);
  // Guard: prevents clearChat from persisting stale content
  const clearingChatRef = useRef(false);

  // Credit balance
  const { balance: creditBalance, refresh: refreshCreditBalance } = useCreditBalance();

  // RAG search feature gating
  const { allowed: ragAllowed, usageCount: ragUsageCount, limit: ragLimit, refresh: refreshRagUsage } = useFeatureAccess('rag');

  // Track online/offline
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load conversation from store
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadFromStore = () => {
      const conversation = getRAGConversation(scopeType, scopeId);
      if (conversation) {
        // Use snake_case field names from database schema
        setMessages(conversation.messages || []);
        setSelectedModel(conversation.preferred_model || DEFAULT_AI_MODEL);
        setQuoteSources(conversation.quote_sources || {});
      }
      setLoading(false);
    };

    const timer = setTimeout(loadFromStore, 100);
    return () => clearTimeout(timer);
  }, [scopeType, scopeId, userId]);

  // Observe changes to conversation store (realtime sync from other devices)
  useObserve(ragConversations$, () => {
    const conversation = getRAGConversation(scopeType, scopeId);
    if (conversation) {
      // Use snake_case field names from database schema
      setMessages(conversation.messages || []);
      setSelectedModel(conversation.preferred_model || DEFAULT_AI_MODEL);
      setQuoteSources(conversation.quote_sources || {});
    }
  });

  // Persist assistant message to store when streaming fully completes.
  // Deferred from onDone to avoid store → useObserve → setMessages race during animation.
  useEffect(() => {
    if (streamingState !== STREAMING_STATES.FULLY_COMPLETE) return;
    if (!pendingStreamContentRef.current) return;
    pendingStreamContentRef.current = false;

    // streamBufferRef has the correct content:
    // - Full content if animation completed normally
    // - Truncated content if user stopped mid-animation
    const content = streamBufferRef.current;
    if (content && !clearingChatRef.current) {
      const msg = { role: 'assistant', content, timestamp: Date.now(), sources: pendingStreamSourcesRef.current };
      addRAGMessage(scopeType, scopeId, msg);
    }
    pendingStreamSourcesRef.current = [];
  }, [streamingState, scopeType, scopeId]);

  // Change model — updates per-scope override + global preference
  const changeModel = useCallback((model) => {
    setSelectedModel(model);
    setRAGPreferredModel(scopeType, scopeId, model);
    updateAIModel(model);
  }, [scopeType, scopeId]);

  // Change response language — persisted to DB via settings$
  const changeLanguage = useCallback((langCode) => {
    setRAGPreferredLanguage(scopeType, scopeId, langCode);
    updateAILanguage(langCode);
  }, [scopeType, scopeId]);

  // Toggle follow UI language — persisted to DB via settings$
  const setFollowUILanguage = useCallback((value) => {
    updateFollowUILanguage(value);
  }, []);

  // Compute effective language: use UI language if followUILanguage is true
  const getEffectiveLanguage = useCallback(() => {
    if (!followUILanguage) return responseLanguage;
    // Map UI language to AI language code (UI uses 'zh', AI uses 'zh-Hans')
    const uiLang = i18n.language?.split('-')[0] || 'en';
    return UI_TO_AI_LANG_MAP[uiLang] || 'en';
  }, [followUILanguage, responseLanguage, i18n.language]);

  const effectiveLanguage = getEffectiveLanguage();

  // Change search sources (global preference)
  const changeSearchSources = useCallback((newSources) => {
    // Ensure at least one source is enabled
    const enabledCount = Object.values(newSources).filter(Boolean).length;
    if (enabledCount === 0) {
      console.warn('At least one search source must be enabled');
      return;
    }

    setSearchSources(newSources);
    try {
      localStorage.setItem('reedle-rag-search-sources', JSON.stringify(newSources));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Change embedding model (global preference)
  const changeEmbeddingModel = useCallback((model) => {
    if (!['qwen', 'openai', 'jina'].includes(model)) {
      console.warn('Invalid embedding model:', model);
      return;
    }
    setEmbeddingModel(model);
    try {
      localStorage.setItem('reedle-rag-embedding-model', model);
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Send message with RAG retrieval
  // extraSystemContext: optional string appended to the system prompt (e.g. current flashcard context)
  const sendMessage = useCallback(async (userMessage, { extraSystemContext } = {}) => {
    if (!userId || !isOnline || sending) return;

    // Check RAG search limit
    if (!ragAllowed) {
      showErrorAlert(
        `Library search limit reached (${ragUsageCount}/${ragLimit} this month). Upgrade to Pro for unlimited searches.`,
        null,
        { component: 'RAGChatContext', action: 'rag_limit_reached', category: ErrorCategory.PAYMENT }
      );
      return;
    }

    // Check credit balance
    if (creditBalance !== null && creditBalance <= 0) {
      openUsageModal('balance', 'rag_chat');
      return;
    }

    // Snapshot streaming preference for this entire request lifecycle
    const isStreaming = captureStreamingMode();
    const controller = prepareFetch();

    setError(null);
    setSending(true);
    setSearchingArticles(true);

    // Add user message immediately
    const userMsg = { role: 'user', content: userMessage, timestamp: Date.now() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    addRAGMessage(scopeType, scopeId, userMsg);

    // Initialize state machine at the assistant message index
    startStreaming(newMessages.length);

    try {
      // Get access token from observable (sync, no network call)
      const session = auth$.session.get();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Call AI chat Edge Function with RAG parameters
      const response = await fetch(
        `${apiConfig.supabaseUrl}/functions/v1/reedle-ai-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: newMessages.map(m => ({ role: m.role, content: m.content })),
            model: selectedModel,
            language: effectiveLanguage,
            // RAG-specific parameters
            useRAG: true,
            ragScope: {
              type: scopeType,
              listId: scopeType === 'list' ? scopeId : null,
              deckId: scopeType === 'deck' ? scopeId : null,
            },
            // Extra system context (e.g. current flashcard in deck review mode)
            ...(extraSystemContext ? { systemContextAppend: extraSystemContext } : {}),
            // Multi-index search sources filter
            ragSources: searchSources,
            // Embedding model for A/B testing
            embeddingModel: embeddingModel,
            // Enable follow-up suggestion buttons
            enableSuggestions: true,
            // Streaming
            stream: isStreaming,
            // Enhanced thinking for Google models
            ...(chatThinkingEnabled && selectedModel?.startsWith('gemini') ? { reasoning_effort: 'high' } : {}),
            // Web search / grounding
            ...(webSearchEnabled ? { enableWebSearch: true } : {}),
          }),
          signal: controller.signal,
        }
      );

      setSearchingArticles(false);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `AI request failed: ${response.status}`);
      }

      // STREAMING PATH
      if (isStreaming && response.headers.get('content-type')?.includes('text/event-stream')) {
        let assistantCreated = false;
        pendingStreamContentRef.current = true;

        await processStreamingResponse(response, {
          updateMessage(content) {
            const msg = { role: 'assistant', content, timestamp: Date.now(), sources: [] };
            if (!assistantCreated) {
              assistantCreated = true;
              setMessages([...newMessages, msg]);
            } else {
              setMessages(prev => [...prev.slice(0, -1), msg]);
            }
            return msg;
          },
          setSending,
          onDone(data, content) {
            const sources = data.sources || [];
            setLastRetrievedSources(sources);
            // Store sources for deferred persistence (useEffect on FULLY_COMPLETE)
            pendingStreamSourcesRef.current = sources;
            // Update React state with final content + sources
            const finalMsg = { role: 'assistant', content, timestamp: Date.now(), sources };
            setMessages(prev => [...prev.slice(0, -1), finalMsg]);
            // Don't persist to store here — deferred to useEffect on FULLY_COMPLETE
            // to avoid store → useObserve → setMessages race during animation
            refreshCreditBalance();
            refreshRagUsage();
          },
          onAbort(content) {
            if (content) {
              const abortedMsg = { role: 'assistant', content, timestamp: Date.now(), sources: [] };
              setMessages(prev => [...prev.slice(0, -1), abortedMsg]);
              // Persist aborted content immediately (no animation to race with)
              addRAGMessage(scopeType, scopeId, abortedMsg);
            }
            pendingStreamContentRef.current = false;
            setSending(false);
          },
          onError(err, content) {
            setError(err.message);
            if (content) {
              const errMsg = { role: 'assistant', content, timestamp: Date.now(), sources: [] };
              setMessages(prev => [...prev.slice(0, -1), errMsg]);
              // Persist error content immediately
              addRAGMessage(scopeType, scopeId, errMsg);
            }
            pendingStreamContentRef.current = false;
            setSending(false);
          },
        });
        return;
      }

      // NON-STREAMING PATH (fallback or streaming preference off)
      const data = await response.json();

      // Store retrieved sources for display
      if (data.sources) {
        setLastRetrievedSources(data.sources);
      }

      // Add assistant message
      const assistantMsg = {
        role: 'assistant',
        content: data.content,
        timestamp: Date.now(),
        sources: data.sources || [],
      };
      const updatedMessages = [...newMessages, assistantMsg];
      setMessages(updatedMessages);
      // Reset streaming state machine so the message renders as ReedleMarkdown
      // (startStreaming was called unconditionally before fetch, leaving state at
      // NETWORK_STREAMING even for non-SSE responses like the no-results early return)
      cleanupStreaming();
      addRAGMessage(scopeType, scopeId, assistantMsg);

      // Refresh credit balance and RAG usage count
      refreshCreditBalance();
      refreshRagUsage();

    } catch (err) {
      if (err.name === 'AbortError') return; // handled by onAbort callback
      console.error('RAG chat error:', err);
      setError(err.message);
      setSearchingArticles(false);
    } finally {
      setSending(false);
    }
  }, [userId, isOnline, sending, messages, selectedModel, effectiveLanguage, scopeType, scopeId, creditBalance, refreshCreditBalance, refreshRagUsage, searchSources, embeddingModel, chatThinkingEnabled, webSearchEnabled, ragAllowed, ragUsageCount, ragLimit, captureStreamingMode, prepareFetch, startStreaming, processStreamingResponse, cleanupStreaming]);

  // Clear conversation
  const clearChat = useCallback(() => {
    clearingChatRef.current = true;
    cleanupStreaming();
    pendingStreamContentRef.current = false;
    pendingStreamSourcesRef.current = [];
    setMessages([]);
    setQuoteSources({});
    setLastRetrievedSources([]);
    clearRAGConversation(scopeType, scopeId);
    clearingChatRef.current = false;
  }, [scopeType, scopeId, cleanupStreaming]);

  // Delete a single message
  const deleteMessage = useCallback((idx) => {
    if (idx < 0 || idx >= messages.length) return;
    deleteRAGMessage(scopeType, scopeId, idx);
    setMessages(prev => prev.filter((_, i) => i !== idx));
  }, [scopeType, scopeId, messages.length]);

  // Get scope display name
  const getScopeDisplayName = useCallback(() => {
    if (scopeType === 'all') return 'All Articles';
    if (scopeType === 'inbox') return 'Inbox';
    if (scopeType === 'deck') return deckName || 'This Deck';
    return 'This List';
  }, [scopeType, deckName]);

  return (
    <RAGChatContext.Provider value={{
      // Scope info
      scopeType,
      scopeId,
      scopeDisplayName: getScopeDisplayName(),
      // Messages
      messages,
      loading,
      sending,
      error,
      // Model selection
      selectedModel,
      changeModel,
      availableModels: AI_MODELS,
      // Language selection
      responseLanguage,
      changeLanguage,
      availableLanguages: RESPONSE_LANGUAGES,
      // Follow UI language
      followUILanguage,
      setFollowUILanguage,
      effectiveLanguage,
      // Search sources filter
      searchSources,
      changeSearchSources,
      // Embedding model for A/B testing
      embeddingModel,
      changeEmbeddingModel,
      availableEmbeddingModels: EMBEDDING_MODELS,
      // Quote sources
      quoteSources,
      // Actions
      sendMessage,
      clearChat,
      deleteMessage,
      // Status
      isOnline,
      // Enhanced thinking toggle
      chatThinkingEnabled,
      updateChatThinking,
      // Web search toggle
      webSearchEnabled,
      updateWebSearch,
      // RAG-specific
      searchingArticles,
      lastRetrievedSources,
      // RAG usage gating
      ragAllowed,
      ragUsageCount,
      ragLimit,
      // Streaming
      streamingState,
      isStreamingActive,
      forceCompleteStreaming,
      handleAnimationComplete,
      stopStreaming,
      streamBufferRef,
      visibleLengthRef,
      streamingMessageIndex,
      streamingEnabled,
      setStreamingEnabled,
    }}>
      {children}
    </RAGChatContext.Provider>
  );
};

export default RAGChatContext;
